import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Money } from '@clari/ui'
import { ErrorBanner } from '@/components/ErrorBanner'
import { apiErrorMessage } from '@/lib/api-error'
import { adminApi } from '@/lib/admin-api'
import { formatDate } from '@/lib/format'

const ACCOUNT_STATUSES = ['opening', 'opened', 'review', 'suspended', 'closed']

export function Accounts() {
  const { t, i18n } = useTranslation('admin')
  const navigate = useNavigate()
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)

  const query = useQuery({
    queryKey: ['admin-accounts', status, page],
    queryFn: () => adminApi.listAccounts({ ...(status ? { status } : {}), page }),
  })
  const totalPages = query.data ? Math.max(1, Math.ceil(query.data.total / query.data.pageSize)) : 1

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-semibold">{t('accounts.title')}</h1>

      <div className="flex flex-wrap items-end gap-3">
        <select
          className="clari-input max-w-xs"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value)
            setPage(1)
          }}
          aria-label={t('accounts.status_filter')}
        >
          <option value="">{t('common.all')}</option>
          {ACCOUNT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`account_statuses.${s}`)}
            </option>
          ))}
        </select>
      </div>

      {query.isError ? <ErrorBanner message={apiErrorMessage(query.error)} /> : null}
      {query.isLoading ? <p className="text-text-muted">{t('common.loading')}</p> : null}

      {query.data ? (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border text-text-muted">
              <tr>
                <th className="px-4 py-2 font-medium">{t('accounts.col_owner')}</th>
                <th className="px-4 py-2 font-medium">{t('accounts.col_iban')}</th>
                <th className="px-4 py-2 font-medium">{t('accounts.col_status')}</th>
                <th className="px-4 py-2 font-medium">{t('accounts.col_balance')}</th>
                <th className="px-4 py-2 font-medium">{t('accounts.col_created')}</th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((a) => (
                <tr
                  key={a.id}
                  onClick={() => navigate(`/accounts/${a.id}`)}
                  className="cursor-pointer border-b border-border last:border-0 hover:bg-bg-subtle"
                >
                  <td className="px-4 py-2 font-mono">{a.owner.email}</td>
                  <td className="px-4 py-2 font-mono">{a.iban}</td>
                  <td className="px-4 py-2">{t(`account_statuses.${a.status}`)}</td>
                  <td className="px-4 py-2">
                    <Money
                      amountCents={a.available_balance_cents}
                      currency={a.currency}
                      locale={i18n.language}
                    />
                  </td>
                  <td className="px-4 py-2 text-text-muted">
                    {formatDate(a.created_at, i18n.language)}
                  </td>
                </tr>
              ))}
              {query.data.items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-text-muted">
                    {t('accounts.empty')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="flex items-center gap-3 text-sm">
        <button
          type="button"
          className="text-text-muted hover:text-text disabled:opacity-40"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          {t('common.prev')}
        </button>
        <span className="text-text-muted">{t('common.page', { page })}</span>
        <button
          type="button"
          className="text-text-muted hover:text-text disabled:opacity-40"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          {t('common.next')}
        </button>
      </div>
    </div>
  )
}
